import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Loader2, CheckCircle2, Mail, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const DISMISS_KEY = "email_verify_dismiss_ts";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // Re-show after 24 hours

type BannerState = "prompt" | "sending" | "sent";

export default function EmailVerificationBanner() {
  const { emailVerified, verificationLoading, sendVerificationEmail, user, isOAuthUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [state, setState] = useState<BannerState>("prompt");

  // Check session-persistent dismiss
  useEffect(() => {
    const stored = sessionStorage.getItem(DISMISS_KEY);
    if (stored) {
      const ts = parseInt(stored, 10);
      if (Date.now() - ts < DISMISS_DURATION_MS) {
        setDismissed(true);
      } else {
        sessionStorage.removeItem(DISMISS_KEY);
      }
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, Date.now().toString());
  }, []);

  const handleSend = useCallback(async () => {
    setState("sending");
    try {
      await sendVerificationEmail();
      setState("sent");
      toast.success("Check your inbox — we sent you a quick verification link!");
    } catch (err: any) {
      setState("prompt");
      toast.error(err.message || "Couldn't send the email right now. Try again shortly.");
    }
  }, [sendVerificationEmail]);

  // Smart suppression: don't show if verified, OAuth user, or no user
  if (emailVerified || isOAuthUser || dismissed || !user) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="bg-gradient-to-r from-primary/[0.06] via-primary/[0.04] to-transparent border-b border-primary/10"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              {state === "sent" ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 text-primary" />
              )}
            </div>

            <p className="text-xs text-foreground/80 leading-relaxed">
              {state === "sent" ? (
                <span className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-primary">Done!</span>
                  We sent a link to <strong className="font-medium">{user.email}</strong> — just tap it to finish setting up.
                </span>
              ) : (
                <span>
                  <span className="font-medium">One quick step left</span>
                  <span className="text-muted-foreground"> — verify your email to unlock your full member experience.</span>
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {state === "prompt" && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSend}
                disabled={verificationLoading}
                className="h-7 text-[11px] gap-1 rounded-full px-3"
              >
                {verificationLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Mail className="w-3 h-3" />
                )}
                Verify Now
                <ArrowRight className="w-3 h-3" />
              </Button>
            )}

            {state === "sent" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setState("prompt")}
                className="h-7 text-[11px] text-muted-foreground hover:text-foreground"
              >
                Resend
              </Button>
            )}

            <button
              onClick={handleDismiss}
              className="p-1 rounded-md text-muted-foreground/50 hover:text-muted-foreground hover:bg-accent transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
